import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { useAuth } from './context/AuthContext';
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  ButtonGroup,
  Row,
  Col,
  Card
} from 'react-bootstrap';

/**
 * 科目管理組件
 * 提供科目的新增、編輯、刪除、查詢功能
 */
function SubjectManagement() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // 狀態管理
  const [subjects, setSubjects] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 篩選條件
  const [filterSsid, setFilterSsid] = useState(user?.e_ssid || '');
  const [filterType, setFilterType] = useState('all'); // all, income, expense
  const [filterActive, setFilterActive] = useState('all'); // all, active, inactive

  // Modal 狀態
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // add, edit
  const [currentSubject, setCurrentSubject] = useState({
    s_id: null,
    ssid: user?.e_ssid || '',
    s_code: '',
    s_title: '',
    s_type: 'expense',
    parent_sid: null,
    is_active: 1
  });

  // 統計資訊
  const [stats, setStats] = useState({
    income: { total: 0, active: 0 },
    expense: { total: 0, active: 0 }
  });

  // 載入社別列表
  const loadSocieties = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSocieties(response.data);
      }
    } catch (err) {
      console.error('載入社別列表失敗:', err);
    }
  };

  // 載入科目列表
  const loadSubjects = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filterSsid) params.ssid = filterSsid;
      if (filterType !== 'all') params.type = filterType;
      if (filterActive === 'active') params.active_only = 'true';

      const response = await axios.get('/finance/subjects', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSubjects(response.data.data);
      }
    } catch (err) {
      setError('載入科目列表失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 載入統計資訊
  const loadStats = async () => {
    try {
      const params = {};
      if (filterSsid) params.ssid = filterSsid;

      const response = await axios.get('/finance/subjects/stats/summary', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const statsData = response.data.data;
        const newStats = {
          income: { total: 0, active: 0, inactive: 0 },
          expense: { total: 0, active: 0, inactive: 0 }
        };

        statsData.forEach(stat => {
          newStats[stat.s_type] = {
            total: stat.total,
            active: stat.active_count,
            inactive: stat.inactive_count
          };
        });

        setStats(newStats);
      }
    } catch (err) {
      console.error('載入統計資訊失敗:', err);
    }
  };

  // 初始化載入
  useEffect(() => {
    loadSocieties();
  }, []);

  useEffect(() => {
    if (filterSsid) {
      loadSubjects();
      loadStats();
    }
  }, [filterSsid, filterType, filterActive]);

  // 開啟新增 Modal
  const handleAdd = () => {
    setModalMode('add');
    setCurrentSubject({
      s_id: null,
      ssid: filterSsid || user?.e_ssid || '',
      s_code: '',
      s_title: '',
      s_type: 'expense',
      parent_sid: null,
      is_active: 1
    });
    setShowModal(true);
  };

  // 開啟編輯 Modal
  const handleEdit = (subject) => {
    setModalMode('edit');
    setCurrentSubject({ ...subject });
    setShowModal(true);
  };

  // 儲存科目
  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!currentSubject.s_code || !currentSubject.s_title) {
        setError('科目代碼和名稱為必填欄位');
        return;
      }

      if (modalMode === 'add') {
        const response = await axios.post('/finance/subjects', currentSubject, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setSuccess('科目新增成功！');
          setShowModal(false);
          loadSubjects();
          loadStats();
        }
      } else {
        const response = await axios.put(`/finance/subjects/${currentSubject.s_id}`, currentSubject, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setSuccess('科目更新成功！');
          setShowModal(false);
          loadSubjects();
          loadStats();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || '儲存失敗');
    }
  };

  // 刪除科目
  const handleDelete = async (id) => {
    if (!window.confirm('確定要刪除此科目嗎？\n（若科目已被使用，將改為停用）')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const response = await axios.delete(`/finance/subjects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        loadSubjects();
        loadStats();
      }
    } catch (err) {
      setError(err.response?.data?.error || '刪除失敗');
    }
  };

  // 切換啟用/停用
  const toggleActive = async (subject) => {
    try {
      const response = await axios.put(
        `/finance/subjects/${subject.s_id}`,
        { is_active: subject.is_active === 1 ? 0 : 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(`科目已${subject.is_active === 1 ? '停用' : '啟用'}`);
        loadSubjects();
        loadStats();
      }
    } catch (err) {
      setError(err.response?.data?.error || '操作失敗');
    }
  };

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">💰 財務管理 - 科目管理</h2>

      {/* 錯誤/成功訊息 */}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* 統計卡片 */}
      <Row className="mb-4">
        <Col md={6}>
          <Card bg="success" text="white">
            <Card.Body>
              <Card.Title>收入類科目</Card.Title>
              <h3>{stats.income.total} 個</h3>
              <small>啟用: {stats.income.active} | 停用: {stats.income.inactive}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card bg="danger" text="white">
            <Card.Body>
              <Card.Title>支出類科目</Card.Title>
              <h3>{stats.expense.total} 個</h3>
              <small>啟用: {stats.expense.active} | 停用: {stats.expense.inactive}</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 篩選工具列 */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>社別篩選 <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={filterSsid}
                  onChange={(e) => setFilterSsid(e.target.value)}
                >
                  <option value="">請選擇社別</option>
                  {societies.map(society => (
                    <option key={society.ss_id} value={society.ss_id}>
                      {society.ss_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={8} className="d-flex align-items-end">
              <ButtonGroup className="me-2">
            <Button
              variant={filterType === 'all' ? 'primary' : 'outline-primary'}
              onClick={() => setFilterType('all')}
            >
              全部
            </Button>
            <Button
              variant={filterType === 'income' ? 'success' : 'outline-success'}
              onClick={() => setFilterType('income')}
            >
              收入
            </Button>
            <Button
              variant={filterType === 'expense' ? 'danger' : 'outline-danger'}
              onClick={() => setFilterType('expense')}
            >
              支出
            </Button>
          </ButtonGroup>

          <ButtonGroup>
            <Button
              variant={filterActive === 'all' ? 'secondary' : 'outline-secondary'}
              onClick={() => setFilterActive('all')}
              size="sm"
            >
              全部狀態
            </Button>
            <Button
              variant={filterActive === 'active' ? 'secondary' : 'outline-secondary'}
              onClick={() => setFilterActive('active')}
              size="sm"
            >
              僅啟用
            </Button>
          </ButtonGroup>

            <Button variant="primary" onClick={handleAdd} disabled={!filterSsid} className="ms-2">
              ➕ 新增科目
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>

      {/* 科目列表 */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : !filterSsid ? (
        <Alert variant="info" className="text-center">
          請選擇社別以查看科目列表
        </Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th style={{ width: '100px' }}>代碼</th>
              <th>科目名稱</th>
              <th style={{ width: '80px' }}>類型</th>
              <th style={{ width: '150px' }}>父科目</th>
              <th style={{ width: '80px' }}>狀態</th>
              <th style={{ width: '200px' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {subjects.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-muted py-4">
                  目前沒有科目資料
                </td>
              </tr>
            ) : (
              subjects.map(subject => (
                <tr key={subject.s_id}>
                  <td><code>{subject.s_code}</code></td>
                  <td>{subject.s_title}</td>
                  <td>
                    <Badge bg={subject.s_type === 'income' ? 'success' : 'danger'}>
                      {subject.s_type === 'income' ? '收入' : '支出'}
                    </Badge>
                  </td>
                  <td className="text-muted small">
                    {subject.parent_title || '-'}
                  </td>
                  <td>
                    <Badge bg={subject.is_active === 1 ? 'success' : 'secondary'}>
                      {subject.is_active === 1 ? '啟用' : '停用'}
                    </Badge>
                  </td>
                  <td>
                    <ButtonGroup size="sm">
                      <Button variant="outline-primary" onClick={() => handleEdit(subject)}>
                        編輯
                      </Button>
                      <Button
                        variant={subject.is_active === 1 ? 'outline-warning' : 'outline-success'}
                        onClick={() => toggleActive(subject)}
                      >
                        {subject.is_active === 1 ? '停用' : '啟用'}
                      </Button>
                      <Button variant="outline-danger" onClick={() => handleDelete(subject.s_id)}>
                        刪除
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* 新增/編輯 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? '新增科目' : '編輯科目'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>科目代碼 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="例如: INC-001"
                    value={currentSubject.s_code}
                    onChange={(e) => setCurrentSubject({ ...currentSubject, s_code: e.target.value })}
                    disabled={modalMode === 'edit'}
                  />
                  <Form.Text className="text-muted">
                    建議格式：INC-XXX (收入) 或 EXP-XXX (支出)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>科目類型 <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={currentSubject.s_type}
                    onChange={(e) => setCurrentSubject({ ...currentSubject, s_type: e.target.value })}
                  >
                    <option value="income">收入</option>
                    <option value="expense">支出</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>科目名稱 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="例如: 常年社友費"
                value={currentSubject.s_title}
                onChange={(e) => setCurrentSubject({ ...currentSubject, s_title: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>父科目（可選，用於階層結構）</Form.Label>
              <Form.Select
                value={currentSubject.parent_sid || ''}
                onChange={(e) => setCurrentSubject({ ...currentSubject, parent_sid: e.target.value || null })}
              >
                <option value="">無（頂層科目）</option>
                {subjects
                  .filter(s => s.s_type === currentSubject.s_type && s.s_id !== currentSubject.s_id)
                  .map(s => (
                    <option key={s.s_id} value={s.s_id}>
                      {s.s_code} - {s.s_title}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="啟用此科目"
                checked={currentSubject.is_active === 1}
                onChange={(e) => setCurrentSubject({ ...currentSubject, is_active: e.target.checked ? 1 : 0 })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {modalMode === 'add' ? '新增' : '更新'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default SubjectManagement;
